import type { PayloadAction } from '@reduxjs/toolkit'
import { createSlice } from '@reduxjs/toolkit'
import type * as Types from './type'
import type { AppDispatch } from '@/store'
import Cookies from 'js-cookie'
import { getUerInfo, logoutApi, getAuthTree, fetchFK } from '@/services/api'
import i18n from '@/i18n'
import { getWalletListApi } from '@/services/api/wallet'

const initialState: Types.IAuthState = {
  isLogined: !!(Cookies.get('osudb_uid') && Cookies.get('osudb_appid')),
  osudb_uid: Cookies.get('osudb_uid') || '',
  osudb_appid: Cookies.get('osudb_appid') || '',
  authResource: null,
  userInfo: null,
  adminTicket: '',
  walletList: null,
  featureKeys: null,
  isShopeeTestUser: false,
}

// 遍历资源树 辅助函数
const traverse = (root, map = {}) => {
  if (root.resourceUrl) {
    map[root.resourceUrl] = true
  }
  if (root?.children) {
    root.children.forEach((item) => {
      traverse(item, map)
    })
  }
  return map
}

// 展开资源树成map 以resourceCode为key
const transferToResourceMap = (resourceTree) => {
  const map = {}
  if (resourceTree) {
    resourceTree.forEach((item) => {
      traverse(item, map)
    })
  }
  return map
}

const authSlice = createSlice({
  name: 'auth',
  initialState,
  reducers: {
    /**
     * 设置登录信息
     * @param state
     * @param action
     * @returns
     */
    setLogin(state, action: PayloadAction<Types.IUserState>) {
      return {
        ...state,
        isLogined: action.payload.isLogined,
        osudb_uid: action.payload.osudb_uid,
        osudb_appid: action.payload.osudb_appid,
      }
    },
    /**
     * 设置权限树
     * @param state
     * @param action
     * @returns
     */
    setAuthResource(state, action: PayloadAction<Types.IAuthState['authResource']>) {
      return {
        ...state,
        authResource: action.payload,
      }
    },
    /**
     * 设置商户信息
     * @param state
     * @param action
     * @returns
     */
    setUserInfo(state, action: PayloadAction<Types.IAuthState['userInfo']>) {
      return {
        ...state,
        userInfo: action.payload,
        isShopeeTestUser: ['5194902040254088194', '5176496960833063946'].includes(action.payload.sellerId),
      }
    },

    /** 钱包列表 */
    setWalletnfo(state, action: PayloadAction<Types.IAuthState['walletList']>) {
      return {
        ...state,
        walletList: action.payload,
      }
    },
    /** 保存超管登录ticket */
    setAdminTicket(state, action: PayloadAction<string>) {
      return {
        ...state,
        adminTicket: action.payload,
      }
    },
    setFeatureKeys(state, action: PayloadAction<any>) {
      return {
        ...state,
        featureKeys: action.payload,
      }
    },
  },
})
export const checkLogin = () => async (dispatch: AppDispatch) => {
  const osudb_uid = Cookies.get('osudb_uid') || ''
  const osudb_appid = Cookies.get('osudb_appid') || ''
  const isLogined = !!(osudb_uid && osudb_appid)
  const newUserState: Types.IUserState = {
    isLogined,
    osudb_uid,
    osudb_appid,
  }
  dispatch(authSlice.actions.setLogin(newUserState))
  return newUserState
}

export const logout = (logoutUrl?: string) => async (dispatch: AppDispatch) => {
  await logoutApi({ appid: process.env.APPID, subappid: process.env.SUB_APPID })
  Cookies.remove('authorization')
  dispatch(authSlice.actions.setLogin({ isLogined: false, osudb_uid: '', osudb_appid: '' }))
  location.href = logoutUrl || `${process.env.ONECLUB_URL}/user/login?scene=logout&app=ow&lang=${i18n.language}`
}

/** 获取用户信息 */
export const getUserInfo = () => async (dispatch: AppDispatch) => {
  const { code, data } = await getUerInfo()
  if (code === 'SUCCESS') {
    dispatch(authSlice.actions.setUserInfo(data))
  }
}

/** 获取钱包信息 */
export const getWalletInfo = () => async (dispatch: AppDispatch) => {
  const { code, data } = await getWalletListApi()
  if (code === 'SUCCESS') {
    dispatch(authSlice.actions.setWalletnfo(data))
  }
}

// 获取权限树的信息
export const getAuthSource = () => async (dispatch: AppDispatch) => {
  const { code, data } = await getAuthTree()
  if (code === 'SUCCESS') {
    const { resourceTree } = data || {}
    const resourceMap = transferToResourceMap(resourceTree)

    dispatch(authSlice.actions.setAuthResource(resourceMap))
  }
}

export const getUserFeatureKeys = () => async (dispatch: AppDispatch) => {
  const fks = await fetchFK()
  const { data } = fks
  dispatch(authSlice.actions.setFeatureKeys(data.featureKeys))
}

export const { setAuthResource, setLogin, setAdminTicket } = authSlice.actions
export default authSlice.reducer
